import type { UnfoldBlock } from '../../lib/types'
import { SourceForm, SourceNote, ThoughtText, type SelectableProps, type SourceProps } from './ThoughtParts'

/**
 * An unfold, as the author sees it (design 4a): a card under the paragraph
 * whose dotted words it hangs from. Detach turns it into a sourced thought in
 * the column and lets go of the words.
 */
export function UnfoldCard({
  block,
  selected,
  sourceFormOpen,
  onSelect,
  onKeyDown,
  onRemove,
  onText,
  onDetach,
  onEditSource,
  onCloseSource,
  onSaveSource,
}: SelectableProps &
  SourceProps & {
    block: UnfoldBlock
    onText: (text: string) => void
    onDetach: () => void
  }) {
  const action = 'whitespace-nowrap font-sans text-[11px] font-medium text-muted-foreground hover:text-primary'
  // The words may carry their own quotation marks; the label adds its own.
  const words = block.words.replace(/^[“”"'‘’\s]+|[“”"'‘’\s]+$/g, '')
  return (
    <div
      data-selectable={block.id}
      tabIndex={0}
      role="group"
      aria-label={`Unfolded under “${block.words}”`}
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
      onKeyDown={onKeyDown}
      className={`relative -mt-3 mb-[26px] flex flex-col gap-2 rounded-2xl border border-card-edge bg-card px-4 py-3.5 font-sans outline-none transition-shadow ${
        selected ? 'ring-2 ring-accent/40' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0 truncate text-[9.5px] font-semibold tracking-[0.08em] text-muted-foreground">UNFOLDED · “{words}”</span>
        <span className="flex shrink-0 items-center gap-3" data-keep-selection>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onDetach()
            }}
            className={action}
          >
            Detach
          </button>
          <button
            type="button"
            aria-label="Remove unfold"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            className={action}
          >
            Remove
          </button>
        </span>
      </div>
      <ThoughtText block={block} onChange={onText} className="font-display text-[15px]/[1.5] text-foreground/85" />
      <SourceNote
        block={block}
        onAdd={() => {
          onSelect()
          onEditSource()
        }}
        className="text-muted-foreground"
      />
      {sourceFormOpen && <SourceForm initial={block.source} own={!!block.own} onCancel={onCloseSource} onSave={onSaveSource} />}
    </div>
  )
}
